/**
 * Per-alarm sound config — which tone and what volume each safety alarm plays.
 *
 * Until now there was one siren for everything. But a skipper needs to tell
 * alarms apart by ear: an anchor dragging or a closing collision risk should
 * sound loud and distinct, while a low-battery nudge shouldn't sound like the
 * boat's about to hit something. So each alarm in ALARM_PRIORITY carries its own
 * tone and volume, set in App & Account → Alarm sound, and the sound runner asks
 * for the top-priority active alarm's choice.
 *
 * Defaults are chosen with that in mind, and they're derived from an alarm's
 * CATEGORY (see alarmCategories.ts) rather than hand-picked one key at a time: the
 * standing-into-danger alarms and a closing collision get the loud, distinct
 * tones at full volume; a GNSS caution is softer; the routine nudges gentler
 * still. Grouping by category is what lets a skipper learn the sounds by ear (all
 * the depth-ish alarms share one sound, all the GNSS ones another) instead of
 * facing a dozen unrelated beeps. The whole map is still persisted and still fully
 * editable per alarm, so a skipper's own choices survive the app being reaped in
 * the background.
 */
import { create } from 'zustand';
import { loadJson, saveJson } from './persist';
import { ALARM_PRIORITY, type AlarmKey } from '@/safety/alarmState';
import { defaultSoundForKey } from '@/ui/alarmCategories';
import type { ToneId } from '@/ui/alarmTones';

/** One alarm's sound: which tone, and how loud (0..1). */
export interface AlarmSoundSetting {
  tone: ToneId;
  volume: number;
}

/** The tone + volume for every alarm key. */
export type AlarmSoundConfig = Record<AlarmKey, AlarmSoundSetting>;

/**
 * Build the full default map straight from each alarm's category (danger /
 * collision / gnss / anchor / notice), so the tone and volume come from one place
 * and every alarm in a category sounds the same. A key not yet placed in a
 * category falls back to the gentle 'notice' sound, so a newly-added alarm gets a
 * safe, quiet default without touching this file.
 */
export const DEFAULT_ALARM_SOUNDS: AlarmSoundConfig = ALARM_PRIORITY.reduce((acc, a) => {
  acc[a.key] = defaultSoundForKey(a.key);
  return acc;
}, {} as AlarmSoundConfig);

const ALARM_SOUNDS_LS = 'acp.alarms.sounds.v1';

interface AlarmSoundsState {
  config: AlarmSoundConfig;
  /** Patch one alarm's tone and/or volume; persists the whole map. */
  setSound: (key: AlarmKey, patch: Partial<AlarmSoundSetting>) => void;
  /** Back to the per-alarm defaults. */
  reset: () => void;
}

// Merge saved OVER defaults so a map written by an older build (fewer alarms)
// still comes back with every current alarm present.
function loadConfig(): AlarmSoundConfig {
  return loadJson<AlarmSoundConfig>(ALARM_SOUNDS_LS, DEFAULT_ALARM_SOUNDS);
}

export const useAlarmSoundsStore = create<AlarmSoundsState>((set) => ({
  config: loadConfig(),
  setSound: (key, patch) =>
    set((s) => {
      const config = { ...s.config, [key]: { ...s.config[key], ...patch } };
      saveJson(ALARM_SOUNDS_LS, config);
      return { config };
    }),
  reset: () => {
    saveJson(ALARM_SOUNDS_LS, DEFAULT_ALARM_SOUNDS);
    return set({ config: DEFAULT_ALARM_SOUNDS });
  },
}));
